<div class="breece-sync-log">
  <!-- Header -->
  <div class="row mb-4 align-items-center">
    <div class="col-md-6">
      <h4>Breece ESL Sync Log</h4>
      <p class="text-muted">Automatic and manual batch sync history for electronic shelf labels</p>
    </div>
    <div class="col-md-6 text-end">
      <span class="text-muted me-3" *ngIf="!isLoading">
        {{ totalItems }} run{{ totalItems !== 1 ? 's' : '' }} total
      </span>
      <button class="btn btn-outline-secondary btn-sm" (click)="loadLogs()">
        <nb-icon icon="refresh-outline"></nb-icon>
        Refresh
      </button>
    </div>
  </div>

  <!-- Table -->
  <nb-card>
    <nb-card-body class="p-0">
      <div class="table-responsive">
        <table class="table table-hover mb-0">
          <thead class="table-light">
            <tr>
              <th>Started At</th>
              <th>Completed At</th>
              <th>Status</th>
              <th class="text-center">Processed</th>
              <th class="text-center">✓ Success</th>
              <th class="text-center">✗ Failed</th>
              <th>Duration</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let log of logs; trackBy: trackByLogId" style="cursor:pointer;" (click)="viewLogDetails(log)">
              <td class="text-nowrap">
                {{ log.startedAt | date:'dd/MM/yyyy HH:mm' }}
              </td>
              <td class="text-nowrap">
                <span *ngIf="log.completedAt; else running">
                  {{ log.completedAt | date:'dd/MM/yyyy HH:mm' }}
                </span>
                <ng-template #running>
                  <span class="text-muted fst-italic">In progress…</span>
                </ng-template>
              </td>
              <td>
                <span class="badge" [ngClass]="getStatusBadgeClass(log.status)">
                  {{ log.status | titlecase }}
                </span>
              </td>
              <td class="text-center">{{ log.productsProcessed ?? '—' }}</td>
              <td class="text-center text-success fw-semibold">{{ log.successCount ?? '—' }}</td>
              <td class="text-center" [class.text-danger]="log.failedCount > 0">
                {{ log.failedCount ?? '—' }}
              </td>
              <td class="text-nowrap">{{ getDuration(log) }}</td>
              <td>
                <button
                  class="btn btn-sm btn-outline-primary"
                  (click)="viewLogDetails(log); $event.stopPropagation()"
                  nbTooltip="View sync details">
                  <nb-icon icon="eye-outline"></nb-icon>
                  View
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Loading State -->
      <div *ngIf="isLoading" class="text-center p-4">
        <nb-spinner size="large"></nb-spinner>
        <p class="mt-2 text-muted">Loading sync logs...</p>
      </div>

      <!-- Error State -->
      <div *ngIf="!isLoading && errorMessage" class="text-center p-4">
        <nb-icon icon="alert-circle-outline" class="text-danger" style="font-size: 2rem;"></nb-icon>
        <p class="mt-2 text-danger">{{ errorMessage }}</p>
        <button class="btn btn-outline-secondary btn-sm" (click)="loadLogs()">
          Try Again
        </button>
      </div>

      <!-- Empty State -->
      <div *ngIf="!isLoading && !errorMessage && logs.length === 0" class="text-center p-4">
        <nb-icon icon="monitor-outline" style="font-size: 2.5rem;" class="text-muted mb-2"></nb-icon>
        <h5>No sync runs yet</h5>
        <p class="text-muted">Automatic and manual Breece batch sync runs will appear here.</p>
      </div>

      <!-- Pagination -->
      <div
        *ngIf="!isLoading && totalPages > 1"
        class="d-flex justify-content-between align-items-center p-3 border-top">
        <div>
          <span class="text-muted">
            Showing {{ (currentPage - 1) * pageSize + 1 }} to
            {{ Math.min(currentPage * pageSize, totalItems) }} of {{ totalItems }} runs
          </span>
        </div>
        <div>
          <button
            class="btn btn-outline-secondary me-2"
            [disabled]="currentPage === 1"
            (click)="onPageChange(currentPage - 1)">
            Previous
          </button>
          <span class="me-2">Page {{ currentPage }} of {{ totalPages }}</span>
          <button
            class="btn btn-outline-secondary"
            [disabled]="currentPage === totalPages"
            (click)="onPageChange(currentPage + 1)">
            Next
          </button>
        </div>
      </div>
    </nb-card-body>
  </nb-card>
</div>

<!-- Log Detail Modal -->
<nb-card *ngIf="showLogDetails" class="modal-overlay">
  <nb-card-header class="d-flex justify-content-between align-items-center">
    <div class="d-flex align-items-center gap-3">
      <h5 class="mb-0">Sync Run Detail</h5>
      <span *ngIf="selectedLog" class="badge" [ngClass]="getStatusBadgeClass(selectedLog.status)">
        {{ selectedLog.status | titlecase }}
      </span>
      <span *ngIf="selectedLog?.startedAt" class="text-muted small">
        {{ selectedLog.startedAt | date:'dd/MM/yyyy HH:mm:ss' }}
      </span>
    </div>
    <button class="btn btn-ghost" (click)="closeLogDetails()">
      <nb-icon icon="close-outline"></nb-icon>
    </button>
  </nb-card-header>

  <nb-card-body *ngIf="selectedLog" class="modal-body">

    <!-- Loading detail -->
    <div *ngIf="loadingDetail" class="text-center p-3">
      <nb-spinner size="medium"></nb-spinner>
      <p class="mt-2 text-muted small">Loading detail...</p>
    </div>

    <div *ngIf="!loadingDetail">
      <!-- Stats Row -->
      <div class="row mb-4">
        <div class="col-md-3 text-center">
          <div class="fs-4 fw-bold">{{ selectedLog.productsProcessed ?? '—' }}</div>
          <small class="text-muted">Processed</small>
        </div>
        <div class="col-md-3 text-center">
          <div class="fs-4 fw-bold text-success">{{ selectedLog.successCount ?? '—' }}</div>
          <small class="text-muted">Succeeded</small>
        </div>
        <div class="col-md-3 text-center">
          <div class="fs-4 fw-bold" [class.text-danger]="selectedLog.failedCount > 0">
            {{ selectedLog.failedCount ?? '—' }}
          </div>
          <small class="text-muted">Failed</small>
        </div>
        <div class="col-md-3 text-center">
          <div class="fs-5 fw-semibold">{{ getDuration(selectedLog) }}</div>
          <small class="text-muted">Duration</small>
        </div>
      </div>

      <!-- Errors Table -->
      <h6 class="section-label">
        Errors
        <span class="badge bg-secondary ms-2">{{ selectedLog.errors?.length || 0 }}</span>
      </h6>

      <div *ngIf="selectedLog.errors && selectedLog.errors.length > 0; else noErrors" class="table-responsive">
        <table class="table table-sm table-bordered">
          <thead class="table-light">
            <tr>
              <th>Product Name</th>
              <th>SKU</th>
              <th>Error Message</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let err of selectedLog.errors">
              <td>{{ err.productName || '—' }}</td>
              <td class="font-monospace small text-muted">{{ err.sku || '—' }}</td>
              <td class="text-danger small">{{ err.message || '—' }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <ng-template #noErrors>
        <div class="alert alert-success d-flex align-items-center gap-2">
          <nb-icon icon="checkmark-circle-2-outline"></nb-icon>
          <span>All products synced successfully.</span>
        </div>
      </ng-template>
    </div>
  </nb-card-body>
</nb-card>
